import { CAMERA_CONFIG, IMAGE_QUALITY_CONFIG } from '../config/constants';
import { captureLimits, isIosDevice, type CaptureBox } from './captureLimits';
import { isLowMemoryError } from './errors';

/** The largest size inside the box that keeps the aspect ratio. Never scales up. */
function fitWithin(width: number, height: number, box: CaptureBox): { width: number; height: number } {
  const scale = Math.min(box.maxWidth / width, box.maxHeight / height, 1);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

function releaseCanvas(canvas: { width: number; height: number }): void {
  // Safari frees a canvas backing store only when the canvas is resized.
  try {
    canvas.width = 1;
    canvas.height = 1;
  } catch {
    // Detached already.
  }
}

function macrotask(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

type Ctx2D = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** Draw into width×height; with a quarter turn the source is a portrait frame turned -90° to fill it. */
function drawFrame(ctx: Ctx2D, source: CanvasImageSource, width: number, height: number, quarterTurn: boolean): void {
  if (quarterTurn) {
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.drawImage(source, -height / 2, -width / 2, height, width);
  } else {
    ctx.drawImage(source, 0, 0, width, height);
  }
}

/**
 * OffscreenCanvas first. A canvas element is the fallback where OffscreenCanvas
 * or its 2d context is missing (jsdom, incomplete implementations).
 */
async function encodeJpegOnce(
  source: CanvasImageSource,
  width: number,
  height: number,
  quarterTurn: boolean
): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined') {
    let offscreen: OffscreenCanvas | undefined;
    try {
      offscreen = new OffscreenCanvas(width, height);
      const ctx = offscreen.getContext('2d');
      if (ctx) {
        drawFrame(ctx, source, width, height, quarterTurn);
        return await offscreen.convertToBlob({ type: 'image/jpeg', quality: CAMERA_CONFIG.JPEG_QUALITY });
      }
    } catch (error) {
      // A low-memory failure must not fall through into a second full-size canvas.
      if (isLowMemoryError(error)) throw error;
      // jsdom / incomplete OffscreenCanvas: canvas element below.
    } finally {
      if (offscreen) releaseCanvas(offscreen);
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');
    drawFrame(ctx, source, width, height, quarterTurn);

    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Failed to convert canvas to blob'))),
        'image/jpeg',
        CAMERA_CONFIG.JPEG_QUALITY
      );
    });
  } finally {
    releaseCanvas(canvas);
  }
}

/** One shot, then one retry. The retry runs after the failed canvas is released. */
async function encodeJpeg(
  source: CanvasImageSource,
  width: number,
  height: number,
  quarterTurn = false
): Promise<Blob> {
  try {
    return await encodeJpegOnce(source, width, height, quarterTurn);
  } catch (error) {
    if (error instanceof Error && error.message === 'Failed to get canvas context') throw error;
    await macrotask();
    return encodeJpegOnce(source, width, height, quarterTurn);
  }
}

/**
 * Decode the camera file. iOS decodes straight to the capture box: a 12 MP
 * (or 48 MP) bitmap is what makes Safari abort with a low-memory error.
 * `resizeWidth` alone keeps the aspect ratio. Other browsers decode full
 * size, and fall back to the same resize only when that fails for memory.
 */
async function decodeBitmap(
  photo: Blob,
  box: CaptureBox,
  ios: boolean
): Promise<{ bitmap: ImageBitmap; resized: boolean }> {
  const resized = async (resizeWidth: number, resizeQuality: ImageBitmapOptions['resizeQuality']) => {
    try {
      return await createImageBitmap(photo, { imageOrientation: 'from-image', resizeWidth, resizeQuality });
    } catch (error) {
      // Safari 16 knows only 'none' | 'flipY' and rejects 'from-image' with a
      // TypeError. Its default 'none' is the pre-2023 name of 'from-image'.
      if (!(error instanceof TypeError)) throw error;
      return createImageBitmap(photo, { resizeWidth, resizeQuality });
    }
  };

  if (ios) {
    try {
      return { bitmap: await resized(box.maxWidth, 'high'), resized: true };
    } catch (error) {
      if (!isLowMemoryError(error)) throw error;
      await macrotask();
      // 1800 still clears 1600×1300 on a 4:3 frame in either orientation.
      return { bitmap: await resized(1800, 'medium'), resized: true };
    }
  }

  try {
    return { bitmap: await createImageBitmap(photo), resized: false };
  } catch (error) {
    if (!isLowMemoryError(error)) throw error;
    await macrotask();
    return { bitmap: await resized(box.maxWidth, 'medium'), resized: true };
  }
}

async function readBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo'));
    reader.readAsArrayBuffer(blob);
  });
}

type TiffEntry = { tag: number; type: number; count: number; valueOffset: number };

/** The entries of the IFD at `ifd` (relative to the TIFF header at `tiff`). */
function readIfd(view: DataView, tiff: number, ifd: number, end: number, little: boolean): TiffEntry[] {
  const start = tiff + ifd;
  if (start + 2 > end) return [];

  const entries: TiffEntry[] = [];
  const count = view.getUint16(start, little);
  for (let i = 0; i < count; i++) {
    const entry = start + 2 + i * 12;
    if (entry + 12 > end) break;
    entries.push({
      tag: view.getUint16(entry, little),
      type: view.getUint16(entry + 2, little),
      count: view.getUint32(entry + 4, little),
      valueOffset: entry + 8,
    });
  }
  return entries;
}

/** An ASCII value; longer than 4 bytes it lives at an offset from the TIFF header. */
function readAscii(view: DataView, tiff: number, end: number, little: boolean, entry: TiffEntry): string | null {
  if (entry.type !== 2) return null;
  const at = entry.count > 4 ? tiff + view.getUint32(entry.valueOffset, little) : entry.valueOffset;
  if (at + entry.count > end) return null;

  let text = '';
  for (let i = 0; i < entry.count; i++) {
    const byte = view.getUint8(at + i);
    if (byte === 0) break;
    text += String.fromCharCode(byte);
  }
  return text;
}

export interface JpegExifHead {
  /** EXIF Orientation, or 0 when there is none. */
  orientation: number;
  /** "YYYY:MM:DD HH:MM:SS" in the camera's local time. */
  dateTimeOriginal: string | null;
  /** "+03:00", when the camera recorded it. */
  offsetTimeOriginal: string | null;
}

const NO_EXIF: JpegExifHead = { orientation: 0, dateTimeOriginal: null, offsetTimeOriginal: null };

function exifFromTiff(view: DataView, tiff: number, end: number): JpegExifHead {
  if (tiff + 8 > end) return NO_EXIF;
  const little = view.getUint16(tiff) === 0x4949;
  const ifd0 = readIfd(view, tiff, view.getUint32(tiff + 4, little), end, little);

  const orientationEntry = ifd0.find((entry) => entry.tag === 0x0112);
  const exifPointer = ifd0.find((entry) => entry.tag === 0x8769);
  const exifIfd = exifPointer ? readIfd(view, tiff, view.getUint32(exifPointer.valueOffset, little), end, little) : [];
  const ascii = (tag: number) => {
    const entry = exifIfd.find((candidate) => candidate.tag === tag);
    return entry ? readAscii(view, tiff, end, little, entry) : null;
  };

  return {
    orientation: orientationEntry ? view.getUint16(orientationEntry.valueOffset, little) : 0,
    dateTimeOriginal: ascii(0x9003),
    offsetTimeOriginal: ascii(0x9011),
  };
}

/**
 * The EXIF fields we need from a JPEG. Only the head of the file is read:
 * APP1 sits before the image data and is at most 64 KB.
 */
export async function readJpegExif(blob: Blob): Promise<JpegExifHead> {
  const view = new DataView(await readBytes(blob.slice(0, 128 * 1024)));
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return NO_EXIF;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    if ((marker & 0xff00) !== 0xff00 || marker === 0xffda) return NO_EXIF;

    const length = view.getUint16(offset + 2);
    // "Exif\0\0", then the TIFF header.
    if (marker === 0xffe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
      return exifFromTiff(view, offset + 10, Math.min(offset + 2 + length, view.byteLength));
    }
    offset += 2 + length;
  }
  return NO_EXIF;
}

/** The EXIF Orientation tag of a JPEG, or 0 when there is none. */
export async function readJpegOrientation(blob: Blob): Promise<number> {
  return (await readJpegExif(blob)).orientation;
}

/**
 * When the camera took this photo, from EXIF DateTimeOriginal (and
 * OffsetTimeOriginal when present; otherwise the device's own time zone,
 * which is the camera's). Null when the file does not record it — gallery
 * screenshots and downloaded images usually do not.
 */
export async function readPhotoTakenAt(blob: Blob): Promise<Date | null> {
  const { dateTimeOriginal, offsetTimeOriginal } = await readJpegExif(blob);
  const match = dateTimeOriginal?.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/);
  if (!match) return null;

  const [, year, month, day, hour, minute, second] = match;
  const offset = offsetTimeOriginal?.match(/^[+-]\d{2}:\d{2}$/) ? offsetTimeOriginal : '';
  const takenAt = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}${offset}`);
  return Number.isNaN(takenAt.getTime()) ? null : takenAt;
}

/**
 * A photo from the native camera (<input capture>), held inside the capture
 * box (4000×3000, or 2400×2400 on iOS and low-memory devices). Phones shoot
 * 12 MP+ (4032×3024 and up), so this is normally a downscale.
 *
 * There is no quarter turn: the native camera's EXIF Orientation records how
 * the phone was really held, so decoding with it applied gives the photo the
 * user framed. The camera's bytes are kept only when they are already a JPEG
 * in range with no rotation to bake in — embedExifIntoJpeg replaces APP1 and
 * would drop the Orientation tag.
 */
export async function boundNativePhoto(photo: Blob): Promise<Blob> {
  const box = currentCaptureBox();
  const ios = typeof navigator !== 'undefined' && isIosDevice(navigator.userAgent, navigator.maxTouchPoints ?? 0);
  // `resized` means the bitmap is not the file's real size, so the original
  // bytes must not be kept — they are the full camera photo.
  const decoded = await decodeBitmap(photo, box, ios);
  try {
    const { width, height } = fitWithin(decoded.bitmap.width, decoded.bitmap.height, box);
    const inRange = width === decoded.bitmap.width && height === decoded.bitmap.height;

    if (!decoded.resized && inRange && photo.type === 'image/jpeg' && (await readJpegOrientation(photo)) <= 1) {
      return photo;
    }

    return await encodeJpeg(decoded.bitmap, width, height);
  } finally {
    decoded.bitmap.close();
  }
}

type PhotoRange = { max: number };
type ImageCaptureLike = {
  takePhoto: (settings?: { imageWidth?: number; imageHeight?: number }) => Promise<Blob>;
  getPhotoCapabilities?: () => Promise<{ imageWidth?: PhotoRange; imageHeight?: PhotoRange }>;
};

/**
 * Ask for the largest photo the capture box keeps. Without PhotoSettings
 * Chromium lets the camera choose, and the Android emulator's camera chose its
 * smallest (320×240) while it offers 1280×960. Capped at the box so a 50 MP
 * sensor is not decoded at full size on a small phone. A camera that rejects
 * the settings gets a plain takePhoto().
 */
async function takeStill(capture: ImageCaptureLike, box: CaptureBox): Promise<Blob> {
  try {
    const caps = await capture.getPhotoCapabilities?.();
    if (caps?.imageWidth?.max && caps.imageHeight?.max) {
      return await capture.takePhoto({
        imageWidth: Math.min(caps.imageWidth.max, box.maxWidth),
        imageHeight: Math.min(caps.imageHeight.max, box.maxHeight),
      });
    }
  } catch (error) {
    console.warn('[Camera] sized takePhoto failed, retrying without settings', error);
  }
  return capture.takePhoto();
}

function imageCaptureConstructor(): (new (track: MediaStreamTrack) => ImageCaptureLike) | undefined {
  return (window as unknown as { ImageCapture?: new (track: MediaStreamTrack) => ImageCaptureLike }).ImageCapture;
}

export type Encoding = { width: number; height: number; quarterTurn: boolean };

/**
 * How to store a frame: landscape, inside the capture box. The app is
 * landscape-only, but the camera follows the display rotation, so with
 * rotation lock on it hands back a portrait frame for a landscape-held phone.
 * Turning those pixels keeps the JPEG matching what the user framed.
 */
export function landscapeEncoding(width: number, height: number, box: CaptureBox = currentCaptureBox()): Encoding {
  if (height > width) return { ...fitWithin(height, width, box), quarterTurn: true };
  return { ...fitWithin(width, height, box), quarterTurn: false };
}

function meetsMinSize({ width, height }: Encoding): boolean {
  return width >= IMAGE_QUALITY_CONFIG.MIN_WIDTH && height >= IMAGE_QUALITY_CONFIG.MIN_HEIGHT;
}

/** Decode ignoring EXIF Orientation, so the output holds sensor pixels. */
async function decodeSensorBitmap(still: Blob): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(still, { imageOrientation: 'none' });
  } catch {
    return await createImageBitmap(still);
  }
}

/**
 * Hold a takePhoto() still to the box and to landscape. Null when it is under
 * the minimum (some phones return their preview size): the caller then uses
 * the video frame.
 */
async function boundStill(still: Blob, box: CaptureBox): Promise<Blob | null> {
  const bitmap = await decodeSensorBitmap(still);
  try {
    const encoding = landscapeEncoding(bitmap.width, bitmap.height, box);
    if (!meetsMinSize(encoding)) return null;

    // Landscape and in range already: the camera's own bytes. embedExifIntoJpeg
    // replaces APP1, so any Orientation tag they carry is dropped with it.
    if (!encoding.quarterTurn && encoding.width === bitmap.width && encoding.height === bitmap.height) {
      return still;
    }

    return await encodeJpeg(bitmap, encoding.width, encoding.height, encoding.quarterTurn);
  } finally {
    bitmap.close();
  }
}

/**
 * The photo from the in-page camera: an ImageCapture still off the track, a
 * separate capture at the camera's photo resolution rather than the stream's,
 * held to the capture box and to landscape. The live video frame stands in
 * when the camera cannot give a usable still.
 *
 * Chromium only: iOS keeps the native camera, because WebKit sizes a still by
 * the video track and rejects PhotoSettings that are not one of its discrete
 * sizes (PR #59).
 */
export async function captureStreamPhoto(track: MediaStreamTrack | undefined, video: HTMLVideoElement): Promise<Blob> {
  const box = currentCaptureBox();
  const ImageCaptureCtor = imageCaptureConstructor();

  if (track && ImageCaptureCtor) {
    try {
      const bounded = await boundStill(await takeStill(new ImageCaptureCtor(track), box), box);
      if (bounded) return bounded;
    } catch (error) {
      console.warn('[Camera] takePhoto failed, using video frame', error);
    }
  }

  const frame = landscapeEncoding(video.videoWidth, video.videoHeight, box);
  return encodeJpeg(video, frame.width, frame.height, frame.quarterTurn);
}

function currentCaptureBox(): CaptureBox {
  if (typeof navigator === 'undefined') return { maxWidth: 4000, maxHeight: 3000 };
  return captureLimits(readDeviceMemory(), navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

function readDeviceMemory(): number | undefined {
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return typeof memory === 'number' ? memory : undefined;
}
